/**
 * Thread polling for the web chat panel (quick-261021-jzd). DOM-free and React-free so it runs
 * under the node vitest env.
 *
 * The panel only hears replies while its own stream is open, so admin / Slack takeover replies
 * after the first one used to stay invisible until a refresh. An OPEN, visible, idle panel now
 * re-reads `GET /thread`: every 5s while a human owns the conversation, and every 20s otherwise.
 * The 20s probe is what catches a takeover that happens after the bot's last reply (mode was
 * still `bot` when the panel loaded); it is the same read, so the mode check and the merge are one
 * code path.
 */

export const THREAD_POLL_HUMAN_MS = 5000;
export const THREAD_POLL_BOT_MS = 20000;

/** `null` means "do not poll". */
export const resolveThreadPollDelayMs = ({
  open,
  humanMode,
  streaming,
  visible,
}: {
  open: boolean;
  humanMode: boolean;
  streaming: boolean;
  visible: boolean;
}): number | null => {
  if (!open || streaming || !visible) return null;
  return humanMode ? THREAD_POLL_HUMAN_MS : THREAD_POLL_BOT_MS;
};

const timeOf = (iso: string): number => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
};

/**
 * Reconcile the panel's rows with a polled server thread.
 *
 * Nothing new -> the SAME array (no re-render). Anything new -> the server rows in server order,
 * plus any local system row newer than the last server turn. Replacing rather than splicing is the
 * simplest correct rule: local ids (done-frame bubbles, optimistic rows) never equal server ids,
 * so keeping them would duplicate bubbles, and the caller only merges when no stream is in flight,
 * so nothing live is lost. An empty server list never wipes the panel.
 */
export const mergeThreadRows = <R extends { kind: string; id: string; createdAt: string }>({
  current,
  server,
}: {
  current: R[];
  server: R[];
}): R[] => {
  if (server.length === 0) return current;

  const currentIds = new Set(current.map((row) => row.id));
  if (server.every((row) => currentIds.has(row.id))) return current;

  const lastServerAt = timeOf(server[server.length - 1].createdAt);
  const trailingSystem = current.filter(
    (row) => row.kind === 'system' && timeOf(row.createdAt) > lastServerAt
  );
  return [...server, ...trailingSystem];
};
